// The stock token art bundled under public/images/tokens, as WebP.
//
// It shipped as PNG until 2026-10, and URLs saved back then still end in .png: monsters.image
// rows, tokens placed on saved maps, chat avatars in the history, cards in My Bestiary.
// tokenArtUrl points those at the WebP file; every other URL passes through untouched.
//
// D&D 5e creatures have full pictures under public/images/bestiary/5e, named by SRD index (see
// content/dnd5eCreatureArt.js). Where one exists it replaces the stock `<index>-token` art, both
// for a token index and for a stored /images/tokens/<index>-token URL, so saved maps pick it up.
//
// Starfinder creatures have their own pictures under public/images/bestiary/starfinder, found by
// creature name (see content/starfinderCreatureArt.js) rather than by index.
//
// All of the bundled raster art is AI-generated - the stock tokens, the 5e and Starfinder
// pictures and the older card artwork - and a user can switch it off (Global Settings, saved as
// the account's showAiArt). Off, a creature gets no bundled picture at all, so it draws its
// initial or placeholder instead; only the loot chest SVGs and uploaded art still show. Stored
// URLs keep pointing at the bundled art either way; tokenArtUrl drops them at render time, so
// switching back on brings every picture back.

import {useSyncExternalStore} from 'react';
import DND5E_CREATURE_ART from '../content/dnd5eCreatureArt';
import STARFINDER_CREATURE_ART from '../content/starfinderCreatureArt';

const STOCK_TOKEN_ART = /^((?:https?:\/\/[^/]+)?\/?)images\/tokens\/([^?#]+)\.(png|webp)$/i;
const AI_ART = /^(?:https?:\/\/[^/]+)?\/?images\/(?:bestiary\/(?:5e|starfinder)\/|card\/|tokens\/[^?#]+\.(?:png|webp)$)/i;

// On unless the user turned it off. ThemeContext sets it from the account's settings.
let aiArtOn = true;
const aiArtListeners = new Set();

export const aiArtEnabled = () => aiArtOn;

export function setAiArtEnabled(on) {
    if (on === aiArtOn) return;
    aiArtOn = on;
    aiArtListeners.forEach(listener => listener());
}

const subscribeAiArt = (listener) => {
    aiArtListeners.add(listener);
    return () => aiArtListeners.delete(listener);
};

/** The AI art switch, re-rendering the caller when it flips so its pictures follow. */
export const useAiArtEnabled = () => useSyncExternalStore(subscribeAiArt, aiArtEnabled);

const tokenSlug = (index) => String(index ?? '').match(/^(.+)-token$/)?.[1];
const has5eArt = (slug) => Boolean(slug) && DND5E_CREATURE_ART.has(slug);

/** The bundled 5e picture for a stock token index such as 'goblin-token', or null. */
export function dnd5eCreatureArt(index) {
    const slug = tokenSlug(index);
    return aiArtOn && has5eArt(slug) ? `/images/bestiary/5e/${slug}.webp` : null;
}

/** The bundled art for a token index, e.g. 'goblin-token': its 5e picture, else the stock token. */
export const stockTokenArt = (index) => tokenArtUrl(stockTokenPath(index));

/**
 * Where the stock token for an index lives, whether or not AI art is on. For values that are
 * stored (chat avatars, Sage monsters) and drawn later through tokenArtUrl.
 */
export const stockTokenPath = (index) => `/images/tokens/${index}.webp`;

/** The older card artwork for a 5e SRD index (not every creature has one), or null. */
export const cardArtwork = (apiIndex) => (aiArtOn && apiIndex ? `/images/card/${apiIndex}-artwork.webp` : null);

const STARFINDER_SYSTEMS = new Set(['STARFINDER', 'STARFINDER_2E']);
const artKey = s => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The bundled picture for a Starfinder creature or token, or null. Matched by name; a token
 * numbered or renamed on the board ("Aeon Guard 2", "Bob") still has the slug in its index.
 */
export function starfinderCreatureArt(creature) {
    if (!aiArtOn || !STARFINDER_SYSTEMS.has(creature?.gameSystem)) return null;
    const name = String(creature.name ?? '');
    const slug = String(creature.index ?? '').replace(/-token$/, '').replace(/^\d+-/, '');
    for (const candidate of [name, name.replace(/\s+\d+$/, ''), slug]) {
        const key = artKey(candidate);
        if (key && Object.hasOwn(STARFINDER_CREATURE_ART, key)) {
            return `/images/bestiary/starfinder/${encodeURIComponent(STARFINDER_CREATURE_ART[key])}.webp`;
        }
    }
    return null;
}

/** The shipped art for a creature or token: its Starfinder picture, else the stock art for its index. */
export const creatureFallbackArt = (creature) => starfinderCreatureArt(creature) ?? stockTokenArt(creature?.index);

/**
 * A stored image URL, with a stock /images/tokens path mapped to its 5e picture when there is one,
 * else an old .png mapped to its WebP. With AI art off, any URL that is or would become AI art is
 * null. Every other URL passes through untouched.
 */
export function tokenArtUrl(url) {
    if (typeof url !== 'string') return url;
    if (!aiArtOn && AI_ART.test(url)) return null;
    const match = url.match(STOCK_TOKEN_ART);
    if (!match) return url;
    const [whole, prefix, stem, ext] = match;
    if (has5eArt(tokenSlug(stem))) return `${prefix}images/bestiary/5e/${tokenSlug(stem)}.webp`;
    return ext.toLowerCase() === 'png' ? `${prefix}images/tokens/${stem}.webp` : whole;
}
